import { useEffect,useState } from 'react';

import config from '@/config';
import useTranslations from '@/i18n/useTranslations';
import TextToggle from '@/shared-components/inputs/components/TextToggle';
import Text from '@/shared-components/text/components/Text';

import CategoryItem from './CategoryItem';
import FilterCategory from './FilterCategory';

import './styles/Size.css';

function CustomSize({ query, setQuery, isMobile }) {
  const t = useTranslations('common.shared.search.filters');
  const [customSize, setCustomSize] = useState(query.size?.isCustomSize ? query.size : { unit: "cm", condition: "smaller", width: "", height: "" });

  function updateCustomSizeValue(key, value) {
    const newCustomSize = { ...customSize, [key]: value };

    setCustomSize(newCustomSize);

    if(isMobile) {
      sendToQuery(newCustomSize);
    }
  }

  function sendToQuery(newCustomSize) {
    if(!newCustomSize.width || !newCustomSize.height) return;

    setQuery({
      ...query, 
      size: {
        ...newCustomSize, 
        isCustomSize: true, 
        displayText: `${newCustomSize.width}${newCustomSize.unit} x ${newCustomSize.height}${newCustomSize.unit}`
      }
    });
  }

  function getPreviewStyles() {
    if(!customSize.width || !customSize.height) return;

    const aspectRatio = customSize.width / customSize.height;

    if(aspectRatio <= 1) {
      return {
        aspectRatio, 
        height: "100%"
      };
    } else {
      return {
        aspectRatio, 
        width: "100%"
      };
    }
  }

  return (
    <>
      <div className="custom-size-choose">
        <TextToggle
          value={customSize.unit} 
          optionA="cm" 
          optionB="inch"
          onChange={(newOption)=>updateCustomSizeValue("unit", newOption)}
        />
      </div>
      <div className="custom-size-dimensions__container">
        <div className="custom-size-dimensions__inputs">
          <div className="custom-size-dimensions__input-container">
            <Text className="custom-size-dimensions__input-header" extraSmall>
              {t('size.labels.width')}
            </Text>

            <div className="custom-size-dimensions__input-box">
              <input 
                type="number" 
                className="custom-size-dimensions__input" 
                defaultValue={customSize.width} 
                autoComplete='off' 
                spellCheck={false} 
                onInput={(e)=>updateCustomSizeValue("width", +e.target.value)}
              />

              <Text className="custom-size-dimensions__input-unit" extraSmallPlus>
                {customSize.unit}
              </Text>
            </div>
          </div>

          <div className="custom-size-dimensions__input-container">
            <Text className="custom-size-dimensions__input-header" extraSmall>
              {t('size.labels.height')}
            </Text>

            <div className="custom-size-dimensions__input-box">
              <input 
                type="number"
                className="custom-size-dimensions__input" 
                defaultValue={customSize.height} 
                autoComplete='off' 
                spellCheck={false} 
                onInput={(e)=>updateCustomSizeValue("height", +e.target.value)}
              />

              <Text className="custom-size-dimensions__input-unit" extraSmallPlus>
                {customSize.unit}
              </Text>
            </div>
          </div>
        </div>

        <div className="custom-size-dimensions__preview-container">
          <div className="custom-size-dimensions__preview" style={getPreviewStyles()}/>
        </div> 
      </div>
      <div className="custom-size-conditions">
        <CategoryItem 
          displayText={t('size.conditions.or_smaller')}
          isSelected={customSize.condition === "smaller"}
          selectItem={()=>updateCustomSizeValue("condition", "smaller")}
        />

        <CategoryItem 
          displayText={t('size.conditions.or_bigger')}
          isSelected={customSize.condition === "bigger"}
          selectItem={()=>updateCustomSizeValue("condition", "bigger")}
        />

        <CategoryItem 
          displayText={t('size.conditions.exact')}
          isSelected={customSize.condition === "exact"}
          selectItem={()=>updateCustomSizeValue("condition", "exact")}
        />
      </div>
      {!isMobile && (
        <Text
          className={[
            `custom-size-search-button ${(!customSize.width || !customSize.height) ? "disabled" : ""}`,
            "font-size-small"
          ].filter(Boolean).join(" ")}
          onClick={()=>sendToQuery(customSize)}>
          {t('size.labels.search')}
        </Text>
      )}
    </>
  );
}

export default function Size({ query, setQuery, isMobile }) {
  const t = useTranslations('common.shared.search.filters');
  const [isCustomSizeMode, setIsCustomSizeMode] = useState(query.size?.isCustomSize);
  const items = config.search.main_search.artwork_filters.size;

  function getSizePresetLabel(item) {
    if(item.displayText === "Small") return t('size.items.small');
    if(item.displayText === "Medium") return t('size.items.medium');
    if(item.displayText === "Large") return t('size.items.large');
    if(item.displayText === "Huge") return t('size.items.huge');

    return item.displayText;
  }

  function isSelectedPreset(item) {
    if(isCustomSizeMode || !query.size) return false;

    return (
      query.size.unit === item.unit &&
      query.size.condition === item.condition &&
      query.size.width === item.width &&
      query.size.height === item.height
    );
  }

  useEffect(()=>{
    if(query.size?.isCustomSize) setIsCustomSizeMode(true);
    else setIsCustomSizeMode(false);
  }, [query.size]);

  return (
    <>
      <FilterCategory 
        className={`size-height-animation ${isCustomSizeMode ? "expanded" : ""}`} 
        category={t('categories.size')}
        hasItems={query.size} 
        isMobile={isMobile}
      >
        <div className="search-filters-content-size__container">
          {items.map((item, index) => {
            const displayText = getSizePresetLabel(item);

            return (
              <CategoryItem 
                key={index} 
                displayText={displayText}
                isSelected={isSelectedPreset(item)}
                selectItem={()=>setQuery({ ...query, size: { isCustomSize: false, ...item, displayText } })}
                removeItem={()=>setQuery({ ...query, size: null })}
              />
            );
          })}

          {/* THE ONLY RESPONSABILITY OF THIS COMPONENT IS TO TRIGGER THE CUSTOM SIZE MODE AND REMOVE THE QUERY VALUE ON REMOVAL IF IT WAS IN THE QUERY */}
          <CategoryItem 
            displayText={t('size.items.custom')}
            isSelected={isCustomSizeMode}
            selectItem={()=>setIsCustomSizeMode(true)}
            removeItem={()=>{
              setIsCustomSizeMode(false);
              if(query.size?.isCustomSize) setQuery({ ...query, size: null });
            }}
          />

          {isCustomSizeMode && (
            <CustomSize 
              query={query} 
              setQuery={setQuery} 
              isMobile={isMobile}
            />
          )}
        </div>
      </FilterCategory>
    </>
  );
}
